import { TrashIcon, XIcon } from '@phosphor-icons/react';
import { captureException } from '@sentry/react';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import useSWR, { useSWRConfig } from 'swr';

import { PageHeader } from '../../../components/PageHeader';
import { Pagination } from '../../../components/pagination/Pagination';
import { Button } from 'components/button/Button';
import { FilterField, FilterPanel, ListPage } from 'components/page-templates/ListPage';
import { Table, ITableHeader, TableSortOrder } from 'components/table/Table';
import { Pill } from 'components/pill/Pill';
import { SimpleSelect } from 'components/select/SimpleSelect';
import { Input } from 'components/input/Input';
import { TimeFrame } from 'components/time-frame/time-frame';
import { TimeFrameState } from 'components/time-frame/types';
import { CheckboxWithLabel } from 'components/checkbox/CheckboxWithLabel';
import { formatDate } from '@utils/date';
import { getDisplayError } from '@utils/get-display-error';
import { fetchEndpointData } from '@utils/fetchEndpointData';
import { ConfirmDialog } from 'components/dialog/ConfirmDialog';
import { useAuth } from 'contexts/auth-context';
import { InternalRole } from 'src/app/auth/endpoints/MeEndpoint.types';
import { roleToNumber } from 'src/app/auth/roles.client';
import { InternalTrailerType } from 'src/types/enums';
import { INTERNAL_TRAILER_TYPE_LABELS, INTERNAL_TRAILER_TYPE_OPTIONS } from '../constants';
import { TrailersHeading } from '../components/TrailersHeading';
import { deleteTrailer, TRAILER_OVERVIEW_SWR_KEY, type TrailerCheckEventTypeType } from '../models';

interface TrailerListItem {
  id: number;
  name: string;
  licensePlate: string;
  brand: string;
  registrationDate: string;
  insuranceExpirationDate: string | null;
  inspectionExpirationDate: string | null;
  isActive: boolean;
  trailerType: InternalTrailerType;
  isRental: boolean;
  rentalCompany: string | null;
  lastCheck: {
    status: 'OK' | 'Damaged';
    cannotContinue: boolean;
    eventType: TrailerCheckEventTypeType;
    eventDate: string;
  } | null;
}

type TrailerSortBy =
  | 'id'
  | 'name'
  | 'lastCheckAt'
  | 'registrationDate'
  | 'insuranceExpirationDate'
  | 'inspectionExpirationDate'
  | 'trailerType'
  | 'rentalCompany';

const EVENT_TYPE_LABELS: Record<TrailerCheckEventTypeType, string> = {
  PickedUp: 'Opgehaald',
  DroppedOff: 'Afgezet',
};

const TABLE_HEADERS: ITableHeader[] = [
  { id: 'id', name: 'ID', sortable: true },
  { id: 'name', name: 'Naam', sortable: true },
  { id: 'licensePlate', name: 'Nummerplaat' },
  { id: 'brand', name: 'Merk' },
  { id: 'trailerType', name: 'Type', sortable: true },
  { id: 'ownership', name: 'Eigendom' },
  { id: 'rentalCompany', name: 'Verhuurmaatschappij', sortable: true },
  { id: 'lastCheckAt', name: 'Laatste check', sortable: true },
  { id: 'registrationDate', name: 'Registratie', sortable: true },
  { id: 'insuranceExpirationDate', name: 'Verzekering', sortable: true },
  { id: 'inspectionExpirationDate', name: 'Keuring', sortable: true },
  { id: 'isActive', name: 'Actief' },
];

const TRAILER_TYPE_FILTER_OPTIONS = [...INTERNAL_TRAILER_TYPE_OPTIONS];

const OWNERSHIP_FILTER_OPTIONS = [
  { key: 'true', name: 'Huurtrailer' },
  { key: 'false', name: 'Eigen' },
];

const PAGE_SIZE = 50;

export const TrailersPage = () => {
  const { me } = useAuth();
  const { mutate: mutateSWR } = useSWRConfig();
  const canDelete = roleToNumber(me.role) >= roleToNumber(InternalRole.InternalAdmin);
  const tableHeaders = useMemo(
    () => (canDelete ? [...TABLE_HEADERS, { id: 'actions', name: '' }] : TABLE_HEADERS),
    [canDelete],
  );
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState('');
  const [trailerTypeFilter, setTrailerTypeFilter] = useState<{ key: string; name: string } | null>(null);
  const [ownershipFilter, setOwnershipFilter] = useState<{ key: string; name: string } | null>(null);
  const [rentalCompanyFilter, setRentalCompanyFilter] = useState('');
  const [isDateFilterActive, setIsDateFilterActive] = useState(false);
  const [timeFrame, setTimeFrame] = useState<TimeFrameState | null>(null);
  const [sortBy, setSortBy] = useState<TrailerSortBy | undefined>('lastCheckAt');
  const [sortOrder, setSortOrder] = useState<TableSortOrder | undefined>('desc');

  const activeTimeFrame = isDateFilterActive ? timeFrame : null;

  const { data, error, isLoading, mutate } = useSWR(
    [
      'trailers',
      offset,
      search,
      trailerTypeFilter?.key,
      ownershipFilter?.key,
      rentalCompanyFilter,
      activeTimeFrame?.startDate,
      activeTimeFrame?.endDate,
      sortBy,
      sortOrder,
    ],
    () => {
      const params = new URLSearchParams();
      params.set('limit', (PAGE_SIZE + 1).toString());
      params.set('offset', offset.toString());
      if (sortBy) params.set('sortBy', sortBy);
      if (sortOrder) params.set('sortOrder', sortOrder);
      if (search) params.set('search', search);
      if (trailerTypeFilter) params.set('trailerType', trailerTypeFilter.key);
      if (ownershipFilter) params.set('isRental', ownershipFilter.key);
      if (rentalCompanyFilter) params.set('rentalCompany', rentalCompanyFilter);
      if (activeTimeFrame) {
        params.set('lastCheckDateFrom', activeTimeFrame.startDate);
        params.set('lastCheckDateTo', activeTimeFrame.endDate);
      }
      return fetchEndpointData<TrailerListItem[]>(`/api/v1/internal/trailer/trailers?${params.toString()}`);
    },
    { keepPreviousData: true },
  );

  const hasNext = (data?.length ?? 0) > PAGE_SIZE;
  const trailers = useMemo(() => (data ?? []).slice(0, PAGE_SIZE), [data]);

  const activeFilterCount = [
    search,
    trailerTypeFilter,
    ownershipFilter,
    rentalCompanyFilter,
    isDateFilterActive && timeFrame,
  ].filter(Boolean).length;

  return (
    <>
      <PageHeader title="Trailers" />

      <ListPage
        heading={<TrailersHeading />}
        filters={
          <FilterPanel
            activeCount={activeFilterCount}
            actions={
              <Button
                size={8}
                color="secondary"
                iconLeft={<XIcon className="button-icon" />}
                onTrigger={() => {
                  setSearch('');
                  setTrailerTypeFilter(null);
                  setOwnershipFilter(null);
                  setRentalCompanyFilter('');
                  setIsDateFilterActive(false);
                  setTimeFrame(null);
                  setOffset(0);
                }}
              >
                Wis alle filters
              </Button>
            }
          >
            <FilterField label="Zoek (TDR ID / naam)">
              <Input
                type="text"
                value={search}
                onChange={(value) => {
                  setSearch(value);
                  setOffset(0);
                }}
                placeholder="Zoek op naam of nummerplaat..."
              />
            </FilterField>

            <FilterField label="Type">
              <SimpleSelect
                selectedItem={trailerTypeFilter}
                items={TRAILER_TYPE_FILTER_OPTIONS}
                onSelect={(item) => {
                  setTrailerTypeFilter(item);
                  setOffset(0);
                }}
                isOptional={true}
              />
            </FilterField>

            <FilterField label="Eigendom">
              <SimpleSelect
                selectedItem={ownershipFilter}
                items={OWNERSHIP_FILTER_OPTIONS}
                onSelect={(item) => {
                  setOwnershipFilter(item);
                  setOffset(0);
                }}
                isOptional={true}
              />
            </FilterField>

            <FilterField label="Verhuurmaatschappij">
              <Input
                type="text"
                value={rentalCompanyFilter}
                onChange={(value) => {
                  setRentalCompanyFilter(value);
                  setOffset(0);
                }}
                placeholder="Filter op verhuurmaatschappij..."
              />
            </FilterField>

            <div className="col-span-full flex flex-col gap-2">
              <CheckboxWithLabel
                isChecked={isDateFilterActive}
                onChange={(checked) => {
                  setIsDateFilterActive(checked);
                  setOffset(0);
                }}
                labelText="Filter op datum laatste check"
              />
              {isDateFilterActive && (
                <TimeFrame
                  onChange={(val) => {
                    setTimeFrame(val);
                    setOffset(0);
                  }}
                />
              )}
            </div>
          </FilterPanel>
        }
        pagination={
          <Pagination
            hasPrevious={offset > 0}
            previous={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            hasNext={hasNext}
            next={() => setOffset(offset + PAGE_SIZE)}
            isFetching={isLoading}
          />
        }
      >
        <Table
          className="h-full"
          idKey="id"
          headers={tableHeaders}
          data={trailers}
          error={error}
          onRetry={() => mutate()}
          isLoading={isLoading}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={(id, order) => {
            setSortBy(id as TrailerSortBy | undefined);
            setSortOrder(order);
            setOffset(0);
          }}
          mapData={(trailer) => {
            const lastCheckBadge = (() => {
              if (!trailer.lastCheck) {
                return <span className="text-fg-muted">Geen checks</span>;
              }

              const color = trailer.lastCheck.cannotContinue
                ? 'negative'
                : trailer.lastCheck.status === 'Damaged'
                  ? 'primary'
                  : 'positive';
              const label = trailer.lastCheck.cannotContinue
                ? 'Stop'
                : trailer.lastCheck.status === 'OK'
                  ? 'OK'
                  : 'Schade';

              return (
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <Pill color={color} size="sm">
                      {label}
                    </Pill>
                    <span className="text-sm text-fg-muted">{formatDate(trailer.lastCheck.eventDate)}</span>
                  </div>
                  <span className="text-xs text-fg-muted">{EVENT_TYPE_LABELS[trailer.lastCheck.eventType]}</span>
                </div>
              );
            })();

            return [
              <Link to={`/internal/trailers/${trailer.id}`} className="link-text">
                {trailer.name || trailer.id}
              </Link>,
              trailer.name || '-',
              trailer.licensePlate,
              trailer.brand,
              INTERNAL_TRAILER_TYPE_LABELS[trailer.trailerType],
              trailer.isRental ? (
                <Pill color="primary" size="sm">
                  Huurtrailer
                </Pill>
              ) : (
                <span className="text-fg-muted">Eigen</span>
              ),
              trailer.rentalCompany || '-',
              lastCheckBadge,
              formatDate(trailer.registrationDate),
              trailer.insuranceExpirationDate ? formatDate(trailer.insuranceExpirationDate) : '-',
              trailer.inspectionExpirationDate ? formatDate(trailer.inspectionExpirationDate) : '-',
              trailer.isActive ? 'Ja' : 'Nee',
              canDelete ? (
                <ConfirmDialog
                  triggerColor="danger"
                  triggerText={<TrashIcon className="button-icon" />}
                  title="Trailer verwijderen?"
                  submitText="Verwijder"
                  description={
                    <div>
                      Ben je zeker dat je trailer <strong>{trailer.name || trailer.licensePlate}</strong> permanent wilt
                      verwijderen? Alle checks, kosten en documenten worden ook verwijderd.
                    </div>
                  }
                  onSubmit={async () => {
                    try {
                      await deleteTrailer(trailer.id);
                      toast.success('Trailer verwijderd');
                      await Promise.all([mutate(), mutateSWR([TRAILER_OVERVIEW_SWR_KEY])]);
                    } catch (err) {
                      captureException(err);
                      toast.error('Kon trailer niet verwijderen: ' + getDisplayError(err));
                    }
                  }}
                />
              ) : null,
            ];
          }}
        />
      </ListPage>
    </>
  );
};
